<script lang="ts">
	// Formatters/i18n
	import { format } from 'src/services/i18n';

	// Svelte
	import { onMount } from 'svelte';
	import { location, querystring, replace } from 'svelte-spa-router';

	// Types
	import type { UnitTransferToken } from 'src/types/UnitTransfer';

	// App Components
	import UnitTransferSignUp from 'src/components/Settings/HomeManagement/UnitTransferSignUp.svelte';
	import UnitTransferDeclineResult from 'src/components/Settings/HomeManagement/UnitTransferDeclineResult.svelte';
	import TransitionWrapper from 'src/components/Utility/TransitionWrapper.svelte';

	// Design system components
	import { Button, Text } from '@Shelly/shelly-design-system/index';

	// Helpers
	import { parseUnitTransferInvite } from 'src/helpers/unit_transfer_invite_helpers';

	import jwtDecode from 'jwt-decode';

	let email = '';
	let token = '';
	let unitId: number | undefined;
	let isDeclineAction = false;
	let linkError = '';
	let ready = false;

	onMount(() => {
		const invite = parseUnitTransferInvite($location, $querystring ?? '');

		if (!invite) {
			console.warn('[RegisterUnitTransfer] could not parse invite from URL', $location);
			navigateToLogin();
			return;
		}
		token = invite.token;
		email = invite.email;
		unitId = invite.unitId;
		isDeclineAction = invite.action === 'decline';

		try {
			const decoded = jwtDecode<UnitTransferToken>(token);

			if (decoded.sub && decoded.sub !== 'unitxfer') {
				throw new Error('Invalid transfer token');
			}

			if (decoded.exp && decoded.exp * 1000 < Date.now()) {
				linkError = $format('error_unit_transfer_invite_expired', {
					default: 'The invitation to take ownership was declined or has expired.',
				});
				return;
			}
		} catch (error) {
			console.error('[RegisterUnitTransfer] invalid unitxfer token', error);
			linkError = $format('error_unit_transfer_invite_invalid', {
				default: 'This home transfer invitation is invalid or has expired.',
			});
			return;
		}

		if (isDeclineAction && unitId == null) {
			linkError = $format('error_unit_transfer_invite_invalid', {
				default: 'This home transfer invitation is invalid or has expired.',
			});
			return;
		}

		ready = true;
	});

	function onDeclineError(event: CustomEvent<string>): void {
		linkError = event.detail;
	}

	function navigateToLogin(): void {
		replace('/login');
	}
</script>

{#if linkError}
	<TransitionWrapper>
		<Text element="h1" class="mb-l">{linkError}</Text>
		<Button
			class="text-capitalize"
			variant="primary"
			width="full"
			aria-label={$format('button_login', { default: 'Log in' })}
			on:click={navigateToLogin}
		>
			{$format('button_login', { default: 'Log in' })}
		</Button>
	</TransitionWrapper>
{:else if ready && isDeclineAction && unitId != null}
	<UnitTransferDeclineResult {token} {email} {unitId} on:error={onDeclineError} />
{:else if ready}
	<UnitTransferSignUp {token} {email} />
{/if}
